import { cn, getMonthIndex, displayCategories, parseDateString } from '@calendar/lib/utils';
import type { Event } from '@/types/calendar';
import type { Category } from '@/types/calendar';
import Container from '../custom/container';
import { FilterState } from '../calendar-filters';

interface ViewGridProps {
  events: Event[]
  className?: string
  categories: Category[]
  filters: FilterState
}

export default function ViewGrid({ events, className, categories, filters }: ViewGridProps) {
  const { month: monthString, season, upcomingOnly } = filters;
  // If upcomingOnly, use current month/year; otherwise use academic season logic
  const now = new Date();
  const month = upcomingOnly
    ? now.getMonth()
    : (getMonthIndex(monthString as string) ?? 0);
  const year = upcomingOnly
    ? now.getFullYear()
    : (() => {
      const [ startYearStr, endYearSuffix ] = season.split('-');
      const startYear = parseInt(startYearStr, 10);
      const endYear = parseInt(`20${endYearSuffix}`, 10);
      const seasonStartStr = window?.curtisCalendar?.seasonStart;
      if (!seasonStartStr) throw new Error('window.curtisCalendar.seasonStart is not defined');
      const [ , seasonStartMonthStr ] = seasonStartStr.split('-').map(Number);
      const seasonStartMonthIndex = seasonStartMonthStr - 1;
      return month >= seasonStartMonthIndex ? startYear : endYear;
    })();

  // Determine today's midnight for upcoming view fading
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Get day names for header
  const dayNames = [ 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday' ];

    // Generate calendar days
    const generateCalendarDays = () => {
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const daysInMonth = lastDay.getDate();

      // Get the day of week for first day (0 = Sunday, 1 = Monday, etc.)
      // Adjust to make Monday the first day (0 = Monday, 6 = Sunday)
      let startDay = firstDay.getDay() - 1;
      if (startDay < 0) startDay = 6;

      // Previous month days to show
      const prevMonthDays = startDay;

      // Array of day objects with events
      const days = [];

      // Previous month faded days
      const prevMonth = month - 1 < 0 ? 11 : month - 1;
      const prevMonthYear = month - 1 < 0 ? year - 1 : year;
      const daysInPrevMonth = new Date(prevMonthYear, prevMonth + 1, 0).getDate();

      for (let i = daysInPrevMonth - prevMonthDays + 1; i <= daysInPrevMonth; i++) {
        days.push({
          date: new Date(prevMonthYear, prevMonth, i),
          dayNumber: i,
          isCurrentMonth: false,
          events: []
        });
      }

      // Current month days with potential fading of past days
      for (let i = 1; i <= daysInMonth; i++) {
        const date = new Date(year, month, i);
        const dayEvents = events.filter(event => {
          const eventDate = parseDateString(event.date);
          return eventDate.getDate() === i &&
                 eventDate.getMonth() === month &&
                 eventDate.getFullYear() === year;
        });
        // In upcoming view, fade days before today
        const isCurrent = !(upcomingOnly && date < todayStart);
        days.push({
          date,
          dayNumber: i,
          isCurrentMonth: isCurrent,
          events: dayEvents
        });
      }

      // Fill remaining grid with next month
      const totalDaysToShow = Math.ceil((prevMonthDays + daysInMonth) / 7) * 7;
      const nextMonthDays = totalDaysToShow - prevMonthDays - daysInMonth;

      const nextMonth = month + 1 > 11 ? 0 : month + 1;
      const nextMonthYear = month + 1 > 11 ? year + 1 : year;

      for (let i = 1; i <= nextMonthDays; i++) {
        days.push({
          date: new Date(nextMonthYear, nextMonth, i),
          dayNumber: i,
          isCurrentMonth: false,
          events: []
        });
      }

      return days;
    };

    const calendarDays = generateCalendarDays();

    // Group days into weeks
    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }

  return (
  <Container className={cn(className, 'calendar-container')}>
    <div className="calendar-wrapper">
      {/* Calendar header with day names */}
      <div className="calendar-header">
        {dayNames.map(day => (
          <div key={day} className="day-name">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="calendar-grid">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="week-row">
            {week.map((day, dayIndex) => (
              <div
                key={dayIndex}
                className="day-cell"
              >
                {/* Day number */}
                <div className={cn(
                  'day-number',
                  !day.isCurrentMonth && 'day-number--inactive',
                  day.isCurrentMonth && 'day-number--active'
                )}>
                  {day.dayNumber}
                </div>

                {/* Events */}
                <div className="events-container">
                  {day.events.map(event => (
                    <div key={event.id} className="event-item">
                      {event.categories?.length > 0 && (
                        <div className="event-category">
                          {displayCategories(event.categories, categories)}
                        </div>
                      )}
                      <div className="event-title">
                        <a href={event.url}>
                          <span className="event-link-overlay"></span>
                          {event.title}
                        </a>
                      </div>
                      <div className="event-time">
                        @ {event.time}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </Container>
  );
}
